<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA ;
    import TaoLogo from '@/components/TaoLogo/TaoLogo.svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import { Link } from '@oat-sa-private/ui-elements';
    import env from '../../../config/env';
    import { NotificationContainer } from '@oat-sa-private/ui-components';

    export const backgroundStyles = {
        none: '',
        white: ' background-filter background-filter-white',
        black: ' background-filter background-filter-black',
        red2orange: ' background-filter background-filter-gradient background-filter-gradient--red-to-orange',
        orange2red: ' background-filter background-filter-gradient background-filter-gradient--orange-to-red',
        blue2red: ' background-filter background-filter-gradient background-filter-gradient--blue-to-red',
        yellow2green: ' background-filter background-filter-gradient background-filter-gradient--yellow-to-green',
    };

    export const infoTextColors = {
        none: 'info-text-color-white',
        white: 'info-text-color-black',
        black: 'info-text-color-white',
        red2orange: 'info-text-color-white',
        orange2red: 'info-text-color-white',
        blue2red: 'info-text-color-white',
        yellow2green: 'info-text-color-black',
    };

    export let logoImage = null;
    export let productName = 'TAO';
    export let title = __('Log in to %s', productName);
    export let linkVisibility = true;
    export let corporateTitle = 'Accelerating innovation in assessment';
    export let corporateText = `For education organizations who want the freedom to control and own their assessment
        experience, from content to technology to delivery to reporting, the TAO assessment platform delivers
        maximum flexibility, interoperability, and security`;
    export let backgroundImage = 'assets/image.png';
    export let backgroundImageVeil = 'red2orange';

    // it handles invalid backgroundImageVeil and sets it to none
    export let backgroundFilter = backgroundStyles[backgroundImageVeil] || backgroundStyles.none;

    if (backgroundImage === '' && ['white', 'none'].includes(backgroundImageVeil)) {
        backgroundFilter = backgroundStyles.none;
    }

    export let infoTextColor = infoTextColors[backgroundImageVeil];
    export let showCompanyText = corporateTitle !== '' || corporateText !== '';

    export const year = new Date().getFullYear();

    export const ver = env('RELEASE_VERSION');
</script>

<style>
    /* horizontally centered layout styles with sticky navbar-wrapper */
    .layout-centered {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: 100%;
        background-size: cover;
        background-image: var(--image);
        position: relative;
        z-index: var(--layer-0);

        &.background-filter {
            /*  colors definitions they're used down below */
            --filter-white: hsla(0, 0%, 100%, 50%);
            --filter-black: hsla(0, 0%, 0%, 55%);
            --gradient-color-blue: #52c4d8;
            --gradient-color-red: #bb2225;
            --gradient-color-orange: #f2a341;
            --gradient-color-yellow: #f6e04b;
            --gradient-color-green: #a7e5cd;

            /*
                the following is a good practice of how to manage css custom properties
                that has default value applied once none is define else where

                [YouTube] Using CSS custom properties like this is a waste
                https://www.youtube.com/watch?v=_2LwjfYc1x8
            */
            --_gradient-opacity: var(--gradient-opacity, 0.75);
            --_gradient-deg: var(--gradient-deg, 135deg);
            --_gradient-from: var(--gradient-from, var(--gradient-color-red));
            --_gradient-to: var(--gradient-to, var(--gradient-color-orange));

            &::before {
                position: absolute;
                content: ' ';
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                z-index: var(--layer-1);

                opacity: var(--_gradient-opacity);
                background-image: linear-gradient(var(--_gradient-deg), var(--_gradient-from), var(--_gradient-to));
            }
        }
    }

    .background-filter-white {
        --gradient-from: var(--filter-white);
        --gradient-to: var(--filter-white);
    }

    .background-filter-black {
        --gradient-from: var(--filter-black);
        --gradient-to: var(--filter-black);
    }

    .background-filter-gradient--red-to-orange {
        --gradient-from: var(--gradient-color-red);
        --gradient-to: var(--gradient-color-orange);
    }

    .background-filter-gradient--orange-to-red {
        --gradient-from: var(--gradient-color-orange);
        --gradient-to: var(--gradient-color-red);
    }

    .background-filter-gradient--blue-to-red {
        --gradient-opacity: 0.75;
        --gradient-from: var(--gradient-color-blue);
        --gradient-to: var(--gradient-color-red);
    }

    .background-filter-gradient--yellow-to-green {
        --gradient-opacity: 0.75;
        --gradient-from: var(--gradient-color-yellow);
        --gradient-to: var(--gradient-color-green);
    }

    .navbar-wrapper {
        position: sticky;
        top: 0;
        width: 100%;
        background-color: white;
        border-bottom: var(--border-thin) solid var(--color-darkSilver);
        height: 6.25rem;
        display: flex;
        z-index: var(--layer-3);
    }

    .navbar-wrapper :global(svg) {
        height: 4.25rem;
        margin-left: 2.5rem;
        margin-top: 1rem;
    }

    .navbar-wrapper :global(header) {
        height: 6.87rem;
    }

    .navbar-content p {
        margin-top: 1.93rem;
        margin-left: 4rem;
        font-weight: 700;
        line-height: 3rem;
        display: none;
    }

    .title-wrapper {
        text-align: center;
        flex: 0 1 auto;

        & h2 {
            margin-top: 3.33rem;
        }
    }

    .container {
        display: flex;
    }

    .login-side {
        min-width: 50%;
        max-width: 75%;
        padding: 4.25rem 4rem 3.62rem 4rem;
    }

    header {
        line-height: 6rem;
        padding: 4rem 0 0;

        & h2 {
            line-height: var(--line-height-heading);
            font-weight: 700;
            margin: 0;
            font-size: var(--fontsize-heading-xxl);
        }
    }

    .login-card {
        background-color: white;
        height: 100%;
        display: flex;
        flex-flow: column;
        padding: 0 1rem;
    }

    .info-text {
        --info-text-white: white;
        --info-text-black: black;
        --info-text-shadow-white: hsla(0, 0%, 100%, 45%);
        --info-text-shadow-black: hsla(0, 0%, 0%, 70%);

        --_info-text-color: var(--info-text-color, var(--info-text-white));
        --_info-text-shadow-color: var(--info-text-shadow-color, var(--info-text-shadow-black));

        flex: 1 0 auto;
        width: 50%;

        padding: 0 10rem 0 15.5rem;

        display: flex;
        flex-direction: column;
        justify-content: center;

        color: var(--_info-text-color);
        text-shadow: 2px 2px 4px var(--_info-text-shadow-color);
    }

    .info-text-color-black {
        --info-text-color: var(--info-text-black);
        --info-text-shadow-color: var(--info-text-shadow-white);
    }

    .info-text-color-white {
        --info-text-color: var(--info-text-white);
        --info-text-shadow-color: var(--info-text-shadow-black);
    }

    .info-text-header {
        font-size: var(--fontsize-heading-xxl);
        @add-mixin bold;
        line-height: 6rem;
        margin-bottom: 3rem;
    }

    .info-text-paragraph {
        font-size: var(--fontsize-body);
        line-height: 3rem;
    }

    main {
        width: 100%;
        height: 100%;
        z-index: var(--layer-2)
    }

    .login-footer-wrapper {
        flex: 0 1 90px;
        margin-top: 4rem;
    }

    .login-links {
        text-align: center;
        margin-bottom: 2.25rem;
    }

    .copyright {
        text-align: center;
        line-height: 2rem;
        font-weight: 400;
        font-size: var(--fontsize-body-xs);
        margin-bottom: 0;
    }

    .display-none {
        display: none;
    }

    .logo {
        max-height: 4rem;
        max-width: 6rem;
        align-self: center;
        margin-left: 1.5rem;
    }

    @media only screen and (--mq-minwidth-medium) {
        main {
            display: flex;
            flex-grow: 1;
        }

        .container {
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 100%;
            align-items: stretch;
        }
    }

    @media only screen and (--mq-maxwidth-medium) {
        .container {
            padding: 1rem;
        }
    }

    @media only screen and (--mq-maxwidth-medium) {
        main {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        .container {
            flex-direction: column;
            justify-content: center;

            padding: 5rem;
        }

        header {
            line-height: 6rem;
            padding: 0 0 6rem;
        }

        .info-text {
            width: 100%;
            padding: 4rem 2rem 2rem 2rem;
        }

        .info-text-header,
        .info-text-paragraph {
            text-align: center;
        }

        .info-text-header {
            font-size: 3.75rem;
            line-height: 5rem;
        }

        .info-text-paragraph {
            line-height: 3rem;
        }

        .login-side {
            width: 100%;
            padding: 0;
            max-width: 100%;
        }

        .login-card {
            width: 100%;
        }

        .hidden-in-mobile {
            display: none;
        }

        .login-card :global(label) {
            padding: 0 2rem 0 2rem;
            width: 100%;
        }

        .info-text-header {
            margin-bottom: 2rem;
        }

        .navbar-content p {
            display: block;
        }
    }

    @media only screen and (--mq-maxwidth-medium) {
        .info-text {
            padding: 4rem 0;
        }
    }

    /*
        handle mobile screen with max height of 450px

        yeh, I know that's not beautiful approach but I didn't find
        any definition at the live design system to handle
        mobile screens for some cases of landscape orientation

        sorry...
    */

    @media only screen and (--mq-maxwidth-small) {
        .container {
            padding: 1rem;
        }
    }

    @media only screen and (--mq-maxwidth-medium) {
        .info-text {
            padding: 1rem 0;
        }

        .info-text-title {
            display: none;
        }
    }
</style>

<NotificationContainer />
<div class="layout-centered {backgroundFilter}" style="--image: url({backgroundImage});">
    <div class="navbar-wrapper">
        {#if logoImage !== false}
            {#if !logoImage}
                <TaoLogo variant="red" />
            {:else}<img src={logoImage} alt="{productName} logo" class="logo" />{/if}
        {/if}
        <div class="navbar-content">
            <p>{title}</p>
        </div>
    </div>
    <main>
        <div class="container">
            {#if showCompanyText}
                <div class="item info-text {infoTextColor}">
                    <p class="info-text-header">{corporateTitle}</p>
                    <p class="info-text-paragraph">{@html corporateText}</p>
                </div>
            {/if}
            <div class="item login-side">
                <div class="login-card">
                    <header class="title-wrapper">
                        <h2 class="hidden-in-mobile">{title}</h2>
                    </header>
                    <slot />
                    <div class="login-footer-wrapper">
                        <p class:display-none={!linkVisibility} class="login-links">
                            <Link
                                label={__('Knowledge Base')}
                                skin="primary"
                                target="blank"
                                href="https://knowledge.taotesting.com/" />
                            <Link
                                label={__('Service Desk')}
                                skin="primary"
                                target="blank"
                                href="https://help.taotesting.com/" />
                        </p>
                        <p class="copyright">
                            © 2013 -
                            {year}
                            · {ver} ·
                            {#if linkVisibility}
                                <a rel="noreferrer" href="https://www.taotesting.com/" target="_blank">Open Assessment
                                    Technologies S.A.</a>
                            {:else}Open Assessment Technologies S.A.{/if}
                            ·
                            {__('All rights reserved.')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </main>
</div>
